import { Button, message, Space, Table, DatePicker, Popconfirm } from "antd";
import { useEffect, useState } from "react";
import { serverFetch } from "../../server";
import { fmtDateTimeFull } from "../../extensions/utils";
import { renderCheckbox, renderDate } from "../../extensions/helper";
import { progKeysStatusRender } from "@extensions/enums";
import LicenseOrderKey from "./license-order-key";
import { useSelector } from "react-redux";
import dayjs from "dayjs";

const LicenseOrderKeys = ({ companyId, accId, onClose }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [finDate, setFinDate] = useState(dayjs());
  const user = useSelector((s) => s.user);

  const getData = () => {
    if (!companyId || !accId) return;
    
    setLoading(true);
    serverFetch(`license/orderkeys?companyId=${companyId}&accId=${accId}`)
      .then((data) => {
        setLoading(false);
        setItems(data);
      })
      .catch((e) => {
        setLoading(false);
        message.error(e.userMessage);
      });
  };

  useEffect(() => { companyId && accId && getData(); }, [companyId, accId]);

  const onRowClick = (record) => {
    setSelectedKey({
      keyId: record.id,
    });
  };

  const handleKeyModalClose = () => {
    setSelectedKey(null);
  };

  const handleSetFinDate = () => {
    setLoading(true);
    
    const requestBody = {
      companyId,
      accId,
      finDate: finDate.format('YYYY-MM-DD')
    };
    
    // Добавляем keyIds только если есть выбранные строки
    if (selectedRowKeys.length > 0) {
      requestBody.keyIds = selectedRowKeys;
    }

    serverFetch(`license/setkeyfindate`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody)
    })
      .then(() => {
        setLoading(false);
        message.success("Дата окончания установлена");
        setSelectedRowKeys([]);
        getData(); // Обновляем данные
      })
      .catch((e) => {
        setLoading(false);
        message.error(e.userMessage);
      });
  };

  const handleSetFrozen = () => {
    setLoading(true);
    
    const requestBody = {
      companyId,
      accId,
      frozen: true
    };
    
    // Добавляем keyIds только если есть выбранные строки
    if (selectedRowKeys.length > 0) {
      requestBody.keyIds = selectedRowKeys;
    }

    serverFetch(`license/setfrozen`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody)
    })
      .then(() => {
        setLoading(false);
        message.success("Ключи заморожены");
        setSelectedRowKeys([]);
        getData(); // Обновляем данные
      })
      .catch((e) => {
        setLoading(false);
        message.error(e.userMessage);
      });
  };

  const handleSetUnfrozen = () => {
    setLoading(true);
    
    const requestBody = {
      companyId,
      accId,
      frozen: false
    };
    
    // Добавляем keyIds только если есть выбранные строки
    if (selectedRowKeys.length > 0) {
      requestBody.keyIds = selectedRowKeys;
    }

    serverFetch(`license/setfrozen`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody)
    })
      .then(() => {
        setLoading(false);
        message.success("Ключи разморожены");
        setSelectedRowKeys([]);
        getData(); // Обновляем данные
      })
      .catch((e) => {
        setLoading(false);
        message.error(e.userMessage);
      });
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  const columns = [
    { dataIndex: "id", title: "#", width: 60 },
    { dataIndex: "status", title: "Статус", render: progKeysStatusRender },
    { dataIndex: "startDate", title: "Активирован", render: renderDate },
    { dataIndex: "finDate", title: "Истекает", render: renderDate },
    { dataIndex: "comment", title: "Комментарий" },
    { dataIndex: "frozen", title: "Заморожен", render: renderCheckbox },
    { dataIndex: "sent", title: "Отправлен", render: renderCheckbox },
  ];

  return (
    <>
      {selectedKey && (
        <LicenseOrderKey
          companyId={companyId}
          accId={accId}
          keyId={selectedKey.keyId}
          onClose={handleKeyModalClose}
        />
      )}
      
      
          {user?.admin && (
            <Space style={{ marginBottom: 16 }}>
              <DatePicker
                format="DD.MM.YYYY"
                value={finDate}
                onChange={setFinDate}
                allowClear={false}
              />
              <Popconfirm
                title="Установить дату окончания"
                description={
                  selectedRowKeys.length === 0 
                    ? `Установить дату окончания ${finDate.format('DD.MM.YYYY')} для всех ключей заказа?`
                    : `Установить дату окончания ${finDate.format('DD.MM.YYYY')} для ${selectedRowKeys.length} выбранных ключей?`
                }
                onConfirm={handleSetFinDate}
                okText="Да"
                cancelText="Отмена"
              >
                <Button 
                  type="primary" 
                  loading={loading}
                >
                  Установить дату окончания
                </Button>
              </Popconfirm>
              
              <Popconfirm
                title="Заморозить ключи"
                description={
                  selectedRowKeys.length === 0 
                    ? "Заморозить все ключи заказа?"
                    : `Заморозить ${selectedRowKeys.length} выбранных ключей?`
                }
                onConfirm={handleSetFrozen}
                okText="Да"
                cancelText="Отмена"
              >
                <Button 
                  type="default" 
                  loading={loading}
                >
                  Заморозить
                </Button>
              </Popconfirm>
              
              <Popconfirm
                title="Разморозить ключи"
                description={
                  selectedRowKeys.length === 0 
                    ? "Разморозить все ключи заказа?"
                    : `Разморозить ${selectedRowKeys.length} выбранных ключей?`
                }
                onConfirm={handleSetUnfrozen}
                okText="Да"
                cancelText="Отмена"
              >
                <Button 
                  type="default" 
                  loading={loading}
                >
                  Разморозить
                </Button>
              </Popconfirm>
            </Space>
          )}
          
          <Table
            columns={columns}
            dataSource={items}
            loading={loading}
            rowKey="id"
            pagination={false}
            size="small"
            className="cloud-modal-table"
            scroll={{ y: 'calc(100vh - 200px)' }}
            rowSelection={rowSelection}
            onRow={(record) => ({ onClick: () => onRowClick(record),style: { cursor: 'pointer' } })}
          />
    </>
  );
};

export default LicenseOrderKeys;
